import { ContainerModule } from 'inversify';

// Inbox Management Bounded Context
import { InboxItemApplicationService } from '../../bounded-contexts/inbox-management';
import { GTDProcessingWorkflowService } from '../../bounded-contexts/inbox-management/domain/services/GTDProcessingWorkflowService';
import { InboxImportExportService } from '../../bounded-contexts/inbox-management/domain/services/InboxImportExportService';
import { InboxItemAggregateRepository } from '../../bounded-contexts/inbox-management/infrastructure/repositories/InboxItemAggregateRepository';

// Project Management Bounded Context
import { ProjectApplicationService } from '../../bounded-contexts/project-management/application/services/ProjectApplicationService';
import { ProjectReadService } from '../../bounded-contexts/project-management/application/services/ProjectReadService';
import { NextActionWriteService } from '../../bounded-contexts/project-management/application/services/NextActionWriteService';
import { NextActionReadService } from '../../bounded-contexts/project-management/application/services/NextActionReadService';
import { ProjectRepository } from '../../bounded-contexts/project-management/infrastructure/repositories/ProjectRepository';
import { NextActionAggregateRepository } from '../../bounded-contexts/project-management/infrastructure/repositories/NextActionAggregateRepository';

// Task Management Bounded Context
import { ITaskRepository } from '../../bounded-contexts/task-management/ports/ITaskRepository';
import { TaskMongoRepository } from '../../bounded-contexts/task-management/infrastructure/repositories/TaskMongoRepository';
import { InboxFileRepository } from '../../bounded-contexts/task-management/infrastructure/repositories/InboxFileRepository';
import { NextActionsFileRepository } from '../../bounded-contexts/task-management/infrastructure/repositories/NextActionsFileRepository';

// Design Implementation Coordination Bounded Context
import { DesignImplementationFlowApplicationService } from '../../bounded-contexts/design-implementation-coordination/application/services/DesignImplementationFlowApplicationService';
import { DesignImplementationFlowReadService } from '../../bounded-contexts/design-implementation-coordination/application/services/DesignImplementationFlowReadService';
import { DesignImplementationFlowRepository } from '../../bounded-contexts/design-implementation-coordination/infrastructure/repositories/DesignImplementationFlowRepository';

// Cross-cutting Services - Commented out due to dependency issues
// import { GtdDomainSeeder } from '../GtdDomainSeeder';

// Import DI types to avoid circular dependencies
import { TYPES } from '../../types/DITypes';

// Note: MongoDB client now handled centrally - no direct imports needed

// Export TYPES for backward compatibility
export const GTD_DOMAIN_SYMBOLS = TYPES;

/**
 * GTD Domain Module - Following Party Manager pattern for clean DI
 * 
 * This function returns a ContainerModule that configures all GTD Domain dependencies
 * following the same pattern as the working Party Manager domain.
 * Uses simple string-based bindings and avoids complex toDynamicValue patterns.
 * 
 * MongoDB client is handled externally and injected where needed.
 */
export const createGTDDomainModule = (): ContainerModule => {
  return new ContainerModule((bind) => {
    // ===================================
    // INFRASTRUCTURE - MongoDB Client
    // ===================================
    // Note: MongoDB client now bound centrally in application core services
    // No need to bind here - just use injected MongoClient

    // ===================================
    // INBOX MANAGEMENT BOUNDED CONTEXT
    // ===================================
    
    // Repository Layer
    bind<InboxItemAggregateRepository>(TYPES.InboxItemAggregateRepository)
      .to(InboxItemAggregateRepository)
      .inSingletonScope();

    // Domain Services
    bind<GTDProcessingWorkflowService>(TYPES.GTDProcessingWorkflowService)
      .to(GTDProcessingWorkflowService)
      .inSingletonScope();

    bind<InboxImportExportService>(TYPES.InboxImportExportService)
      .to(InboxImportExportService)
      .inSingletonScope();

    // Application Services
    bind<InboxItemApplicationService>(TYPES.InboxItemApplicationService)
      .to(InboxItemApplicationService)
      .inSingletonScope();

    // ===================================
    // PROJECT MANAGEMENT BOUNDED CONTEXT
    // ===================================
    
    // Repository Layer
    bind<ProjectRepository>(TYPES.ProjectRepository)
      .to(ProjectRepository)
      .inSingletonScope();

    bind<NextActionAggregateRepository>(TYPES.NextActionAggregateRepository)
      .to(NextActionAggregateRepository)
      .inSingletonScope();

    // Application Services (CQRS)
    bind<NextActionWriteService>(TYPES.NextActionWriteService)
      .to(NextActionWriteService)
      .inSingletonScope();

    bind<NextActionReadService>(TYPES.NextActionReadService)
      .to(NextActionReadService)
      .inSingletonScope();

    bind<ProjectApplicationService>(TYPES.ProjectApplicationService)
      .to(ProjectApplicationService)
      .inSingletonScope();

    bind<ProjectReadService>(TYPES.ProjectReadService)
      .to(ProjectReadService)
      .inSingletonScope();

    // ===================================
    // TASK MANAGEMENT BOUNDED CONTEXT
    // ===================================
    
    // Repository Layer
    bind<ITaskRepository>(TYPES.ITaskRepository)
      .to(TaskMongoRepository)
      .inSingletonScope();

    bind<TaskMongoRepository>(TYPES.TaskMongoRepository)
      .to(TaskMongoRepository)
      .inSingletonScope();

    // File-based Repositories
    bind<InboxFileRepository>(TYPES.InboxFileRepository)
      .to(InboxFileRepository)
      .inSingletonScope();

    bind<NextActionsFileRepository>(TYPES.NextActionsFileRepository)
      .to(NextActionsFileRepository)
      .inSingletonScope();

    // ===================================
    // DESIGN IMPLEMENTATION COORDINATION BOUNDED CONTEXT
    // ===================================
    
    // Repository Layer
    bind<DesignImplementationFlowRepository>(TYPES.DesignImplementationFlowRepository)
      .to(DesignImplementationFlowRepository)
      .inSingletonScope();

    // Application Services
    bind<DesignImplementationFlowApplicationService>(TYPES.DesignImplementationFlowApplicationService)
      .to(DesignImplementationFlowApplicationService)
      .inSingletonScope();

    bind<DesignImplementationFlowReadService>(TYPES.DesignImplementationFlowReadService)
      .to(DesignImplementationFlowReadService)
      .inSingletonScope();
  });
};